/**
 * useAuth Hook
 * 
 * Main authentication hook providing:
 * - User information
 * - Authentication status
 * - Permission checking
 * - Login/logout functions
 */

import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { AppDispatch } from '@core/store/store';
import {
  selectIsAuthenticated,
  selectIsLoading,
  selectUser,
  selectPermissions,
  selectError,
} from '../store/authSelectors';
import { logout as logoutAction } from '../store/authSlice';

export const useAuth = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectIsLoading);
  const user = useSelector(selectUser);
  const permissions = useSelector(selectPermissions);
  const error = useSelector(selectError);

  /**
   * Check if user has a specific permission
   * @param permission - Permission string (e.g., "Fleet.Vehicle.Read")
   * @returns true if user has the permission
   */
  const can = useCallback(
    (permission: string): boolean => {
      if (!isAuthenticated) return false;
      return permissions.includes(permission);
    },
    [isAuthenticated, permissions]
  );

  /**
   * Check if user has any of the specified permissions
   * @param permissionsToCheck - Array of permission strings
   * @returns true if user has at least one permission
   */
  const canAny = useCallback(
    (permissionsToCheck: string[]): boolean => {
      if (!isAuthenticated) return false;
      return permissionsToCheck.some((p) => permissions.includes(p));
    },
    [isAuthenticated, permissions]
  );

  /**
   * Check if user has all of the specified permissions
   * @param permissionsToCheck - Array of permission strings
   * @returns true if user has all permissions
   */
  const canAll = useCallback(
    (permissionsToCheck: string[]): boolean => {
      if (!isAuthenticated) return false;
      return permissionsToCheck.every((p) => permissions.includes(p));
    },
    [isAuthenticated, permissions]
  );

  /**
   * Logout the current user
   */
  const logout = useCallback(() => {
    void dispatch(logoutAction());
  }, [dispatch]);

  return {
    // State
    isAuthenticated,
    isLoading,
    user,
    permissions,
    error,

    // Permission checking
    can,
    canAny,
    canAll,

    // Actions
    logout,
  };
};
